import { Platform } from 'react-native'
import {
  put,
  call,
  select,
  all,
  spawn,
  take,
  delay,
  retry,
  cancelled,
  race,
  cancel,
  fork,
  SagaReturnType,
  Effect,
} from 'redux-saga/effects'
import { eventChannel } from 'redux-saga'
import { AnyAction } from 'redux'
import * as RNLocalize from 'react-native-localize'
import Toast from 'react-native-toast-message'
import moment, { Moment } from 'moment'
import merge from 'deepmerge'
// Redux Actions
import { LoginTypes } from '../../Redux/LoginRedux'
import WellnessCenterActions, { WellnessCenterTypes, Models } from 'APP/Redux/WellnessCenterRedux'
import { PatientTypes } from '../../Redux/PatientRedux'
import { AppSessionTypes } from '../../Redux/AppSessionRedux'
import { StartupTypes } from '../../Redux/StartupRedux'
// Services
import Config from 'APP/Config'
import I18n from 'APP/I18n'
import { WellnessCenter } from '@dialogue/services'
import * as AppleHealthKit from 'APP/Lib/AppleHealthKit'
import * as TrackerHelpers from 'APP/Lib/TrackerHelpers'
import { transformDateLimit, formatDateToISOString } from '../../Lib/DateHelpers'
import { getHealthKitCache, setHealthKitCache } from 'APP/Lib/AppleHealthKit'
import { navigationRef as Nav } from 'APP/Nav'
import { checkRenewAuth } from '../AuthSagas'
import { contentActions, contentSelectors } from 'APP/Store/Content'
import { logger } from 'APP/Lib/Datadog'
import { RootState } from 'APP/Store/CreateStore'
import {
  Service as ChallengesService,
  GetParticipantsParams,
  GetTeamParticipantsParams,
} from '@dialogue/services/dist/wellness-center/challenges/service'
import { Participant } from '@dialogue/services/dist/wellness-center/challenges/model'
import { Service as TrackersService } from '@dialogue/services/dist/wellness-center/trackers/service'
import { Service as UsersService } from '@dialogue/services/dist/wellness-center/users/service'
import {
  TrackerConnection,
  TrackerName,
  TrackerInfo,
} from '@dialogue/services/dist/wellness-center/trackers/model'
import { ActivityName } from '@dialogue/services/dist/wellness-center/challenges/model'
import { WellnessCenterState } from 'APP/Store/WellnessCenter/types'
import { selectIsAppActive } from '../AppSessionSagas'

const { Challenges, Trackers, Users } = WellnessCenter

export function* createWellnessCenter(): Generator<
  any,
  {
    Challenges: ChallengesService
    Trackers: TrackersService
    Users: UsersService
  },
  any
> {
  const accessToken = yield select(selectAccessToken)

  return {
    Challenges: new Challenges.Service(accessToken, Config.WELLNESSCENTER_SERVICE_URL),
    Trackers: new Trackers.Service(accessToken, Config.WELLNESSCENTER_SERVICE_URL),
    Users: new Users.Service(accessToken, Config.WELLNESSCENTER_SERVICE_URL),
  }
}

export const selectIsBackgrounded = (state: RootState) => !selectIsAppActive(state)
export const selectAccessToken = (state: RootState) => state.login.accessToken
export const selectPatientId = (state: RootState) => state.patient.profile.id
export const selectPatientProfile = (state: RootState) => state.patient.profile
export const selectPatientActiveEligibilityIntervals = (state: RootState) =>
  (state.patient.profile.eligibilityIntervals || []).filter(
    (record: { status: string }) => record.status === 'active'
  )
export const selectSelectedChallenge = (state: RootState) => state.wellnessCenter.selectedChallenge
export const selectTrackers = (state: RootState) => state.wellnessCenter.trackers

export const selectUserTrackerByStatus = (state: RootState, trackerStatus: string) => {
  const { trackers } = selectTrackers(state)
  return trackers.user.filter(({ status }) => status == trackerStatus)
}

export const selectShouldTrackerSyncBeIgnored = (state: RootState) => {
  const profile = state.patient?.profile
  const isLoggedIn = profile?.id ? true : false
  if (!isLoggedIn) {
    return 'User is not logged in'
  }

  const curRouteName = Nav.getCurrentRoute()?.name
  const isSyncAllRouteForbidden = SYNC_ALL_SAMPLES_FORBIDDEN_SCENES.some(
    (forbiddenSceneName) => curRouteName === forbiddenSceneName
  )
  if (isSyncAllRouteForbidden) {
    return `Sync is forbidden for this route: ${curRouteName}`
  }

  // no syncing or token refresh allowed in background for members with forcedSessionInactivity
  const isBackgrounded = selectIsBackgrounded(state)
  if (isBackgrounded && profile?.forcedSessionInactivityRequired) {
    return 'forcedSessionInactivity enabled'
  }

  return undefined
}

export const selectAllTrackersToSync = (
  state: RootState,
  { keys, lastSyncTimeThreshold }: { keys?: string[]; lastSyncTimeThreshold?: Moment | string }
) => {
  const trackerKeys = keys ? keys : [WellnessCenter.Trackers.Types.SdkTrackerName.AppleHealthKit]
  let trackersByStatus = selectUserTrackerByStatus(
    state,
    WellnessCenter.Trackers.Types.TrackerStatus.Connected
  )

  return trackersByStatus.reduce((trackers: TrackerConnection[], tracker: TrackerConnection) => {
    const { tracker_name, last_sync_time } = tracker
    const lastSyncTimeUTC = moment.utc(last_sync_time)
    const isTrackerKeyIncluded = trackerKeys.includes(tracker_name)
    const isLastSyncTimeThresholdReached = lastSyncTimeThreshold
      ? moment(lastSyncTimeUTC).isSameOrBefore(lastSyncTimeThreshold)
      : true
    const needsToBeSync = isTrackerKeyIncluded && isLastSyncTimeThresholdReached
    return needsToBeSync ? [...trackers, tracker] : trackers
  }, [])
}

// Tictrac requires "en-us" for English content.
const generateLangKey = (lang: string) => (lang === 'en' ? 'en-us' : lang)

function* selectPlanTags(): Generator {
  const eligibilityIntervals: SagaReturnType<typeof selectPatientActiveEligibilityIntervals> =
    yield select(selectPatientActiveEligibilityIntervals)
  // planTags is used to filter challenges from Tictrac API ([g]org:plan_id_xx is their formatting)
  return eligibilityIntervals.map((record) => `[g]org:plan_id_${record.planId}`)
}

export function* getMyChallenges(): Generator {
  try {
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const data: SagaReturnType<typeof wellnessCenter.Users.getChallenges> = yield call(
      wellnessCenter.Users.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
        tag: yield selectPlanTags(),
        status: [
          Challenges.Types.ChallengeStatus.UPCOMING,
          Challenges.Types.ChallengeStatus.ONGOING,
        ],
        sort: Challenges.Types.Sort.END_DATE_ASC,
      }
    )

    yield put(WellnessCenterActions.getMyChallengesSuccess(data))
  } catch (_) {
    yield put(WellnessCenterActions.getMyChallengesFailure())
  }
}

export function* getCompletedChallenges(): Generator {
  try {
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const data: SagaReturnType<typeof wellnessCenter.Users.getChallenges> = yield call(
      wellnessCenter.Users.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
        tag: yield selectPlanTags(),
        status: [Challenges.Types.ChallengeStatus.COMPLETE],
        sort: Challenges.Types.Sort.END_DATE_DESC,
        from_date: moment().subtract(2, 'month').format(I18n.t('DateFormat')),
      }
    )

    yield put(WellnessCenterActions.getCompletedChallengesSuccess(data))
  } catch (_) {
    yield put(WellnessCenterActions.getCompletedChallengesFailure())
  }
}

export function* getNewChallenges(): Generator {
  try {
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const data: SagaReturnType<typeof wellnessCenter.Challenges.getChallenges> = yield call(
      wellnessCenter.Challenges.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
        tag: yield selectPlanTags(),
        status: [
          Challenges.Types.ChallengeStatus.UPCOMING,
          Challenges.Types.ChallengeStatus.ONGOING,
        ],
        sort: Challenges.Types.Sort.END_DATE_ASC,
        participant: false,
      }
    )

    yield put(WellnessCenterActions.getNewChallengesSuccess(data))
  } catch (_) {
    yield put(WellnessCenterActions.getNewChallengesFailure())
  }
}

export function* getChallenge({ challengeId }: { challengeId: string }): Generator {
  try {
    if (!challengeId) throw Error('missing required parameters')
    const parsedId = parseInt(challengeId, 10)

    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )
    const { TrackerStatus } = WellnessCenter.Trackers.Types

    const challenge: SagaReturnType<typeof wellnessCenter.Challenges.getChallenge> = yield call(
      wellnessCenter.Challenges.getChallenge,
      {
        challengeId: parsedId,
        lang: [generateLangKey(I18n.baseLocale)],
      }
    )

    // Capture if user has joined the challenge already
    const allUserChallenges: SagaReturnType<typeof wellnessCenter.Users.getChallenges> = yield call(
      wellnessCenter.Users.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
      }
    )

    const userChallenge = allUserChallenges.find((uc) => uc.id === parsedId)
    const inChallenge = userChallenge !== undefined

    // Check if user has at least one tracker connected
    const userTrackers: SagaReturnType<typeof wellnessCenter.Users.getUsersTrackers> = yield call(
      // @ts-expect-error - the userTrackers doen't have the userId property
      wellnessCenter.Users.getUsersTrackers,
      {
        userId: yield select(selectPatientId),
      }
    )
    const connectedTrackers = userTrackers.filter(
      (tracker) => tracker.status === TrackerStatus.Connected
    )
    const trackerIsBroken = userChallenge
      ? (userTrackers || []).find(
          (tracker) => tracker.tracker_name === userChallenge.participant.tracker_name
        )?.status === TrackerStatus.Broken
      : false

    const userHasTracker = connectedTrackers.length > 0 && !trackerIsBroken

    // Check if all connected user trackers are not compatible
    let userHasNoCompatibleTrackers = false
    if (userHasTracker) {
      const trackers: SagaReturnType<typeof wellnessCenter.Trackers.getTrackers> = yield call(
        wellnessCenter.Trackers.getTrackers,
        {}
      )
      const trackerActivitiesMap = trackers.reduce<Record<TrackerName, ActivityName[]>>(
        (acc, tracker: TrackerInfo) => ({ ...acc, [tracker.tracker_name]: tracker.activities }),
        {} as Record<TrackerName, ActivityName[]>
      )
      userHasNoCompatibleTrackers = !connectedTrackers.some((tracker: TrackerConnection) =>
        (trackerActivitiesMap[tracker.tracker_name] || []).includes(challenge.activity)
      )
    }

    let userChallengeGoal =
      userChallenge && userHasTracker ? userChallenge?.participant?.total_value || 0 : 0

    yield put(
      WellnessCenterActions.getChallengeSuccess(
        challenge,
        inChallenge,
        userHasTracker,
        userHasNoCompatibleTrackers,
        userChallengeGoal
      )
    )
  } catch (_) {
    yield put(WellnessCenterActions.getChallengeFailure())
  }
}

export function* watchForUpdateUserProfileOpportunity(action: () => Generator): Generator {
  while (true) {
    yield race({
      onStartup: all([take(StartupTypes.STARTUP), take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS)]),
      onAppForegrounded: all([
        take(AppSessionTypes.SET_APP_FOREGROUNDED),
        take(PatientTypes.PATIENT_PROFILE_FETCH_SUCCESS),
      ]),
      onLogin: all([
        take(LoginTypes.LOGIN_SUCCESS),
        take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS),
      ]),
      onProfileUpdate: take(PatientTypes.PATIENT_PROFILE_UPDATE_SUCCESS),
    })
    yield call(action)
  }
}

export function* updateUserProfile(): Generator {
  try {
    const isAuthenticated: boolean = yield select(
      (state: RootState) => !!state.patient?.profile?.id
    )

    if (!isAuthenticated) {
      return
    }
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const patientProfile: SagaReturnType<typeof selectPatientProfile> = yield select(
      selectPatientProfile
    )

    // if first or last name is not set there is a
    // data integrity issue or maybe a state issue.
    // in this case only update the timezone in WC.
    const firstName = patientProfile?.preferredName || patientProfile?.givenName
    const lastName = patientProfile?.familyName
    const displayName = !firstName || !lastName ? null : `${firstName} ${lastName}`

    // @ts-expect-error FIXME: there is no timezone param in the UserUpdate interface
    yield call(wellnessCenter.Users.updateUserProfile, {
      ...(displayName && { display_name: displayName, first_name: firstName, last_name: lastName }),
      timezone: RNLocalize.getTimeZone(),
    })
  } catch (error) {
    //no user feedback
    logger.error('Failed to update user profile', {}, error)
  }
}

export function* joinIndividualChallenge({ challengeId }: { challengeId: string }): Generator {
  try {
    if (!challengeId) throw Error('missing required parameters')

    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    // Add user to challenge
    // @ts-expect-error FIXME: the addParticipantToTeam challengeId is typed as number
    yield call(wellnessCenter.Challenges.addParticipant, { challengeId })

    yield call(getLeaderboard, { challengeId })

    // Refresh main list of challenges
    yield put(WellnessCenterActions.getMyChallenges())
    yield put(WellnessCenterActions.getNewChallenges())
    yield call(getChallenge, { challengeId })

    yield put(WellnessCenterActions.joinIndividualChallengeSuccess(true))
  } catch (_) {
    yield put(WellnessCenterActions.joinIndividualChallengeFailure())
  }
}

export function* joinTeamChallenge({
  challengeId,
  teamId,
}: {
  challengeId: string
  teamId: string
}): Generator {
  try {
    if (!challengeId || !teamId) throw Error('missing required parameters')

    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    // Add user to team
    // @ts-expect-error FIXME: the addParticipantToTeam challengeId and teamId properties are typed as number
    yield call(wellnessCenter.Challenges.addParticipantToTeam, {
      challengeId,
      teamId,
    })

    yield call(getLeaderboard, { challengeId })

    // Refresh main list of challenges
    yield put(WellnessCenterActions.getMyChallenges())
    yield put(WellnessCenterActions.getNewChallenges())
    yield call(getChallenge, { challengeId })

    yield put(WellnessCenterActions.joinTeamChallengeSuccess(true))
  } catch (_) {
    yield put(WellnessCenterActions.joinTeamChallengeFailure())
    Toast.show({ text1: I18n.t('WellnessCenter.challenges.retryJoin') })
  }
}

type FetchMemberExplicitlyArgs = (
  | {
      method: (params: GetParticipantsParams) => Promise<Participant[]>
      uniqueMethodArgs: GetParticipantsParams
    }
  | {
      method: (params: GetTeamParticipantsParams) => Promise<Participant[]>
      uniqueMethodArgs: GetTeamParticipantsParams
    }
) & {
  patientId: string
  userRank: number
  page: number
}

export function* fetchMemberExplicitly({
  method,
  uniqueMethodArgs,
  patientId,
  userRank,
  page,
}: FetchMemberExplicitlyArgs): Generator {
  // Our perfect pageSize is 4 members above our current user rank, so that we can return +/- 3
  // participants in a single API call. HOWEVER there are two edge cases:
  // 1. userRank is significantly smaller than 97 (too small API response could mean many API calls)
  // 2. userRank is signficantly higher than 100 (too large API response could timeout)
  let pageSize = userRank - 4

  // Edge cases. 90 and 250 feel like safe numbers
  if (pageSize <= 90 || pageSize >= 250) {
    pageSize = 100
  }

  let moreParticipants = []
  let memberIndex = -1
  let fetch = 1
  const maxFetch = 20

  while (memberIndex === -1) {
    moreParticipants = yield call(method, {
      ...uniqueMethodArgs,
      pageSize,
      page: page + 1,
    })

    if (moreParticipants.length === 0 || fetch >= maxFetch) {
      break
    }

    // @ts-expect-error FIXME: there is no such field on the Participant interface
    memberIndex = moreParticipants.findIndex((p) => p.user.external_id === patientId)
    fetch = fetch += 1
  }

  return moreParticipants.slice(memberIndex - 3, memberIndex + 4)
}

type FetchAndFilterAndGroupParticipantsArgs =
  | (
      | {
          method: (params: GetParticipantsParams) => Promise<Participant[]>
          uniqueMethodArgs: GetParticipantsParams
        }
      | {
          method: (params: GetTeamParticipantsParams) => Promise<Participant[]>
          uniqueMethodArgs: GetTeamParticipantsParams
        }
    ) & {
      userIsInChallenge: boolean
      userRank?: number
    }

function* fetchAndFilterAndGroupParticipants({
  method,
  uniqueMethodArgs,
  userIsInChallenge,
  userRank,
}: FetchAndFilterAndGroupParticipantsArgs): Generator {
  const pageSize = 100 // we'll show top 50ish, so this is slightly larger so we can use index in our logic below.
  const participants: Participant[] = yield call(method, {
    ...uniqueMethodArgs,
    pageSize,
    page: 0,
  })

  // Rank !== index here, and rank is not unique (many users can be the same rank)
  // so it's important to use the index here for the first few usecases to
  // ensure we *always* show the member in the UI.
  // REMEMBER index starts at 0 so numbers are 1 lower than expected.
  const patientId = yield select(selectPatientId)

  let top50ishParticipants: Participant[] = []
  let memberBasedParticipants: Participant[] = []

  // Usecase 1. Member is not in challenge, show basic top 50
  if (!userIsInChallenge) {
    top50ishParticipants = participants.slice(0, 50)
  }

  // @ts-expect-error FIXME: there is no such field on the Participant interface
  const memberIndex = participants.findIndex((p) => p.user.external_id === patientId.toString())

  // Usecase 2. Member is in challenge and is index 0-46, show basic top 50
  if (userIsInChallenge && memberIndex <= 46) {
    top50ishParticipants = participants.slice(0, 50)
  }

  // Usecase 3. Member is in challenge and is index 47-53, show up to member & 3 more people afterwards (max 56)
  if (userIsInChallenge && memberIndex >= 47 && memberIndex <= 53) {
    top50ishParticipants = participants.slice(0, memberIndex + 4)
  }

  // Usecase 4. Member is in challenge and is index 54-(end of participant list - 4), show top 50 AND second list of 7 with member in the middle
  if (userIsInChallenge && memberIndex >= 54 && memberIndex <= pageSize - 4) {
    top50ishParticipants = participants.slice(0, 50)
    memberBasedParticipants = participants.slice(memberIndex - 3, memberIndex + 4)
  }

  // Usecase 5. Member is in challenge and index is greater than (end of participant list - 4), show top 50 AND second list of 7 with member in the middle
  if (userIsInChallenge && (memberIndex >= pageSize - 3 || memberIndex === -1)) {
    top50ishParticipants = participants.slice(0, 50)

    memberBasedParticipants = yield call<typeof fetchMemberExplicitly>(fetchMemberExplicitly, {
      method,
      uniqueMethodArgs,
      page: 0,
      patientId: patientId.toString(),
      // @ts-expect-error FIXME: userRank is not allowed to be undefined in fetchMemberExplicitly
      userRank,
    })
  }

  return { participants: top50ishParticipants, additionalParticipants: memberBasedParticipants }
}

export function* getLeaderboard({ challengeId }: { challengeId: string }): Generator {
  try {
    if (!challengeId) throw Error('missing required parameters')
    const parsedId = parseInt(challengeId, 10)

    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const { challenge } = yield select(selectSelectedChallenge)

    const isIndividualChallenge = challenge.type === Challenges.Types.ChallengeType.INDIVIDUAL

    const allUserChallenges: SagaReturnType<typeof wellnessCenter.Users.getChallenges> = yield call(
      wellnessCenter.Users.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
      }
    )
    const userChallenge = allUserChallenges.find((uc) => uc.id === parsedId)
    const userIsInChallenge = userChallenge !== undefined

    let participants = []
    let additionalParticipants = []

    if (isIndividualChallenge) {
      const individualParticipants = yield call(fetchAndFilterAndGroupParticipants, {
        method: wellnessCenter.Challenges.getParticipants,
        uniqueMethodArgs: {
          challengeId: parsedId,
        },
        userIsInChallenge,
        userRank: userChallenge?.participant?.rank,
      })

      participants = individualParticipants.participants
      additionalParticipants = individualParticipants.additionalParticipants
    } else {
      participants = yield call(wellnessCenter.Challenges.getTeams, {
        challengeId: parsedId,
        lang: [generateLangKey(I18n.baseLocale)],
      })
    }

    const activeLeaderboardItem = new Models.ActiveLeaderboardItem(
      userChallenge?.participant?.user?.id,
      userChallenge?.team?.id
    )

    yield put(
      WellnessCenterActions.getLeaderboardSuccess(
        participants,
        additionalParticipants,
        activeLeaderboardItem
      )
    )
  } catch (error) {
    logger.error('Failed to get leaderboard', { challengeId }, error)
    yield put(WellnessCenterActions.getLeaderboardFailure())
  }
}

export function* getTeamParticipants({
  challengeId,
  teamId,
}: {
  challengeId: number
  teamId: string
}): Generator {
  try {
    if (!challengeId || !teamId) throw Error('missing required parameters')

    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )

    const allUserChallenges: SagaReturnType<typeof wellnessCenter.Users.getChallenges> = yield call(
      wellnessCenter.Users.getChallenges,
      {
        lang: [generateLangKey(I18n.baseLocale)],
      }
    )
    const userChallenge = allUserChallenges.find((uc) => uc.id === challengeId)

    const { participants, additionalParticipants } = yield call(
      fetchAndFilterAndGroupParticipants,
      {
        method: wellnessCenter.Challenges.getTeamParticipants,
        uniqueMethodArgs: {
          challengeId,
          teamId: teamId as unknown as number,
        },
        userIsInChallenge: true,
        userRank: userChallenge?.participant?.rank,
      }
    )

    yield put(
      WellnessCenterActions.getTeamParticipantsSuccess(participants, additionalParticipants)
    )
  } catch (error) {
    logger.error('Failed to get team participants', { challengeId, teamId }, error)
    yield put(WellnessCenterActions.getTeamParticipantsFailure())
  }
}

export function* leaveSelectedChallenge(): Generator {
  try {
    const { challenge } = yield select((state) => state.wellnessCenter.selectedChallenge)
    if (!challenge) {
      throw new Error('challenge must be defined')
    }
    if (
      ![
        Challenges.Types.ChallengeStatus.ONGOING,
        Challenges.Types.ChallengeStatus.UPCOMING,
      ].includes(challenge.status)
    ) {
      throw new Error(
        `challenge status must be ${[
          Challenges.Types.ChallengeStatus.ONGOING,
          Challenges.Types.ChallengeStatus.UPCOMING,
        ]}`
      )
    }
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )
    yield call(wellnessCenter.Challenges.removeUser, {
      challengeId: challenge.id,
    })

    // Refresh challenges (list + current one)
    yield all([
      put(WellnessCenterActions.getMyChallenges()),
      put(WellnessCenterActions.getNewChallenges()),
      call(getChallenge, { challengeId: challenge.id }),
    ])
    yield put(WellnessCenterActions.leaveSelectedChallengeSuccess())
  } catch (error) {
    yield put(WellnessCenterActions.leaveSelectedChallengeFailure())
    logger.error('Failed to leave challenge', {}, error)
    Toast.show({ text1: I18n.t(`WellnessCenter.challenges.leaveError`) })
  }
}

export function* getTrackers_legacy(): Generator {
  try {
    const wellnessCenter: SagaReturnType<typeof createWellnessCenter> = yield call(
      createWellnessCenter
    )
    const { TrackerStatus } = WellnessCenter.Trackers.Types

    const [trackers, userTrackers]: [
      SagaReturnType<typeof wellnessCenter.Trackers.getTrackers>,
      SagaReturnType<typeof wellnessCenter.Users.getUsersTrackers>
    ] = yield all([
      call(wellnessCenter.Trackers.getTrackers, {}),
      call(wellnessCenter.Users.getUsersTrackers, {}),
    ])

    const SUPPORTED_TRACKERS: Record<string, string> = {
      strava: 'strava',
      garmin: 'garmin',
    }
    const REMOVED_TRACKERS: Record<string, string> = {}

    const DEPRECATED_TRACKERS: Record<string, string> = {
      // Google fit will be supported just until the end of 2026.
      google_fit: 'google_fit',
      fitbit: 'fitbit',
    }

    // Add support for Apple Health for iOS users only, remove it entirely for Android users
    if (Platform.OS === 'ios') {
      SUPPORTED_TRACKERS.apple_healthkit = 'apple_healthkit'
    } else {
      REMOVED_TRACKERS.apple_healthkit = 'apple_healthkit'
    }

    const user = (userTrackers || []).filter(
      ({ status, tracker_name }) =>
        status !== TrackerStatus.Disconnected && !REMOVED_TRACKERS[tracker_name]
    )
    const supported: TrackerInfo[] = []
    const comingSoon: TrackerInfo[] = []

    // Update base trackers list to remove trackers user has already connected & ones explicitly not supported
    const plainTrackers: TrackerInfo[] = trackers.filter(
      ({ tracker_name }) =>
        !user.some((ut) => ut.tracker_name === tracker_name) &&
        !REMOVED_TRACKERS[tracker_name] &&
        !DEPRECATED_TRACKERS[tracker_name]
    )

    plainTrackers.forEach((tracker: TrackerInfo) => {
      // Separate supported trackers from those coming soon.
      if (SUPPORTED_TRACKERS[tracker.tracker_name] !== undefined) {
        supported.push(tracker)
        return
      }

      comingSoon.push(tracker)
    })
    yield put(WellnessCenterActions.getTrackersSuccess({ user, supported, comingSoon }))
  } catch (error) {
    logger.error('Failed to get trackers', {}, error)
    yield put(WellnessCenterActions.getTrackersFailure())
    throw error as Error
  }
}

/**
 * @todo deprecated callback pattern see https://dialoguemd.atlassian.net/browse/DIA-43082
 */
export function* connectTracker({
  trackerName,
  id,
  callback,
}: {
  trackerName: TrackerName
  id?: string
  callback: (connectionId: string | null, error?: Error | null) => void
}): Generator {
  try {
    const { Users }: SagaReturnType<typeof createWellnessCenter> = yield call(createWellnessCenter)
    const connectionId: SagaReturnType<typeof Users.createUsersTrackerConnection> = yield call(
      Users.createUsersTrackerConnection,
      {
        trackerName,
        id,
      }
    )
    yield call(getTrackers_legacy)
    callback && (yield call(callback, connectionId))
  } catch (error) {
    logger.error('Failed to connect tracker', { trackerName, trackerId: id }, error)
    callback && (yield call(callback, null, error as Error))
  }
}

/**
 * @todo deprecated callback pattern see https://dialoguemd.atlassian.net/browse/DIA-43082
 */
export function* disconnectTracker({
  trackerName,
  callback,
}: {
  trackerName: string
  callback: (error: Error | null) => void
}): Generator {
  try {
    const { Users } = yield call(createWellnessCenter)
    yield call(Users.deleteUsersTrackerConnection, {
      trackerName,
    })
    yield call(getTrackers_legacy)
    callback && (yield call(callback, null))
  } catch (error) {
    logger.error('Failed to disconnect tracker', { trackerName }, error)
    callback && (yield call(callback, error as Error))
  }
}

type AddMetricsTrackerParams = {
  trackerName: string
  trackerActivities: {
    activity: string
    activity_time: string
    metrics: { metric: string; value: number }[]
  }[]
  fromDate: string | null
  toDate: string | null
  syncDate?: string
  maxSize?: number
  callback?: (error: Error | null) => void
}

/**
 * @todo deprecated callback pattern see https://dialoguemd.atlassian.net/browse/DIA-43082
 */
export function* addMetricsTracker({
  trackerName,
  trackerActivities,
  fromDate,
  toDate,
  syncDate,
  callback,
}: AddMetricsTrackerParams): Generator {
  try {
    const { Users } = yield call(createWellnessCenter)
    yield retry(3, 500, Users.addUsersTrackerMetric, {
      trackerName,
      trackerActivities,
      fromDate,
      toDate,
      syncDate,
    })
    callback && (yield call(callback, null))
  } catch (error) {
    logger.error('Failed to add tracker metric', {}, error)
    callback && (yield call(callback, error as Error))
    throw error
  }
}

/**
 * Helper function for syncAllSamplesForAppleHealthKit
 * @param activities array of activities for single day
 * @param cache array of strings for single day
 * @returns {boolean} if syncAllSamplesForAppleHealthKit should proceed
 */
export const dateAlreadySynced = (
  activities: { unique_id: string }[],
  cache?: string[]
): boolean => {
  const isCacheNotStale = activities.every(({ unique_id }: { unique_id: string }) =>
    cache?.includes(unique_id)
  )
  const isCacheNew = activities.length === 0 && cache === undefined
  const hasSameActivitesLength = activities.length === cache?.length

  return hasSameActivitesLength ? isCacheNotStale : isCacheNew
}

/**
 * Helper function for syncAllSamplesForAppleHealthKit
 * @param startDate start of the timeframe
 * @param cache map of activities per day
 * @returns {string} the date to start the sync from
 */
export const adjustStartDateRangeBasedOnCache = (
  startDate: string,
  cache: Record<string, string[]>
): string => {
  const cacheStartDate = Object.keys(cache).sort()[0]

  return cacheStartDate && moment(cacheStartDate).isAfter(startDate, 'day')
    ? cacheStartDate
    : startDate
}

/**
 * Sync all samples of Apple Health Kit.
 * Computing all samples could increase memory usage and cause a crash.
 * Controlling the number of API call can be done by leveraging `interval` and `maxSize`.
 * Recommendation is to use an interval of 1 day to avoid taking risk of loading too much samples.
 * Caching results in adjusments of the startDate, as there's a limit how many activities can be cached
 * if there's any cache the BE is already populated with data, but not all updates would be synced.
 * The more activities - the smaller window of days back is kept up to date
 */
export function* syncAllSamplesForAppleHealthKit({
  startDate: requestedStartDate,
  endDate,
  interval,
  maxSize,
}: {
  startDate: string
  endDate: string
  interval?: [number, string]
  maxSize?: number
}): Generator {
  try {
    const healthKitActivitiesCache = yield call(getHealthKitCache)
    const newHealthKitActivitiesCache: Record<string, string[]> = {}

    const startDate = adjustStartDateRangeBasedOnCache(requestedStartDate, healthKitActivitiesCache)
    const mapDateRangeOptions = {
      startDate: formatDateToISOString(moment(startDate).startOf('day')),
      endDate: formatDateToISOString(transformDateLimit(moment(endDate).endOf('day'), moment())),
      interval: interval || [1, 'days'],
    }

    const metricsQueued: AddMetricsTrackerParams = {
      trackerName: WellnessCenter.Trackers.Types.TrackerName.AppleHealthkit,
      fromDate: null,
      toDate: null,
      maxSize: maxSize || 100,
      trackerActivities: [],
    }

    const waitAndSync = function* (): Generator {
      const now = moment()
      // check if the roundup to seconds made the toDate in the furure
      // if so delay by the diff so BE validation does not throw
      if (moment(metricsQueued.toDate).isAfter(now)) {
        yield delay(moment(metricsQueued.toDate).diff(now))
      }
      yield spawn(addMetricsTracker, merge.all<AddMetricsTrackerParams>([{}, metricsQueued]))
      metricsQueued.trackerActivities = []
      metricsQueued.fromDate = null
    }

    const itDateRanges = TrackerHelpers.makeDateRangeIterator(mapDateRangeOptions)

    for (let [startDate, endDate] of itDateRanges) {
      const { fromDate, toDate } = AppleHealthKit.transformSamplesOptionsToAddMetricsParams({
        startDate,
        endDate,
      })

      const cacheDate = moment(startDate).format(I18n.t('DateFormat'))

      const activities: Awaited<ReturnType<typeof AppleHealthKit.getAllActivitySamples>> =
        yield call(AppleHealthKit.getAllActivitySamples, { startDate, endDate })
      const cache = healthKitActivitiesCache[cacheDate]

      if (dateAlreadySynced(activities, cache)) {
        // chop the block when cached days are encountered
        if (metricsQueued.fromDate) {
          yield waitAndSync()
        }

        continue
      }

      const activitiesLength = metricsQueued.trackerActivities.push(...activities)
      metricsQueued.fromDate = metricsQueued.fromDate ? metricsQueued.fromDate : fromDate
      metricsQueued.toDate = toDate

      if (activities.length) {
        newHealthKitActivitiesCache[cacheDate] = activities.map(({ unique_id }) => unique_id)
      }

      if (metricsQueued.maxSize && metricsQueued.maxSize <= activitiesLength) {
        yield waitAndSync()
      }
    }

    if (Object.keys(newHealthKitActivitiesCache).length > 0) {
      yield call(setHealthKitCache, { ...healthKitActivitiesCache, ...newHealthKitActivitiesCache })
    }

    if (metricsQueued.fromDate) {
      yield waitAndSync()
    }
  } catch (error) {
    logger.error(
      'Failed to sync Apple HealthKit data',
      { endDate, interval, maxSize, requestedStartDate },
      error
    )
    throw error
  }
}

export const SYNC_ALL_SAMPLES_LAST_SYNC_THRESHOLD = [30, 'seconds']

export const SYNC_ALL_SAMPLES_FORBIDDEN_SCENES = [
  'conversation',
  'newConversation',
  'incomingCall',
  'videoCall',
]

export const SYNC_ALL_SAMPLES_APPLE_HEALTH_KIT_ARGS = (now: moment.Moment) => ({
  startDate: now.clone().startOf('day').subtract(3, 'months').toISOString(),
  endDate: now.toISOString(),
})

/**
 * Sync all samples for every connected trackers.
 * @param {Object} options
 * @param {object} options.trackersWithSyncOptions - Record containing some tracker name and associated options.
 * @param {boolean} options.needsSyncTrackerStatus - Boolean to ensure `getTrackers` is refetch and populated.
 * @param {array} options.lastSyncTimeThreshold - Tuple represention to prevent sync a tracker if last_sync_time is not reached.
 *
 * @example
 * // Synchronize all connected trackers.
 * // Error handling is detached when trackerWithSyncOptions is not defined.
 * syncAllSamplesForTrackers()
 *
 * @example
 * // Prevent synchronize all connected trackers if it was already synchronize less than 5 minutes ago.
 * syncAllSamplesForTrackers({
 *   lastSyncTineThreshold: [5, 'minutes']
 * })
 *
 * @example
 * // Synchronize some connected trackers.
 * // Error handling is not detached.
 * syncAllSamplesForTrackers({
 *   trackersWithSyncOptions: {
 *      apple_healthkit: { startDate, endDate }
 *    }
 * })
 */
type SyncAllSamplesForTrackersOptions = {
  trackersWithSyncOptions?: Record<string, { startDate: string; endDate: string }>
  needsSyncTrackerStatus?: boolean
  lastSyncTimeThreshold?: [moment.DurationInputArg1, moment.DurationInputArg2]
}

export function* syncAllSamplesForTrackers({
  trackersWithSyncOptions,
  needsSyncTrackerStatus,
  lastSyncTimeThreshold,
}: SyncAllSamplesForTrackersOptions): Generator {
  try {
    const now = moment()

    const needsToBeSpawned = trackersWithSyncOptions ? false : true

    const applyOptionOrDefaultByTracker = (tracker_name: string) => {
      if (trackersWithSyncOptions && trackersWithSyncOptions[tracker_name]) {
        return trackersWithSyncOptions[tracker_name]
      }
      const applySyncActionArgs = SYNC_ALL_SAMPLES_APPLE_HEALTH_KIT_ARGS
      return applySyncActionArgs(now)
    }

    if (needsSyncTrackerStatus) {
      yield call(getTrackers_legacy)
    }

    const trackers = yield select(selectAllTrackersToSync, {
      keys: trackersWithSyncOptions ? Object.keys(trackersWithSyncOptions) : undefined,
      lastSyncTimeThreshold: lastSyncTimeThreshold
        ? now.clone().subtract(...lastSyncTimeThreshold)
        : undefined,
    })

    const syncAllSamplesActions = trackers.reduce(
      (actions: Effect[], { tracker_name }: { tracker_name: string }): Effect[] => {
        if (tracker_name === WellnessCenter.Trackers.Types.SdkTrackerName.AppleHealthKit) {
          const syncAction = syncAllSamplesForAppleHealthKit
          const syncOptions = applyOptionOrDefaultByTracker(tracker_name)
          const syncEffect = needsToBeSpawned
            ? spawn(syncAction, syncOptions)
            : call(syncAction, syncOptions)
          actions.push(syncEffect)
        }
        return actions
      },
      [] as Effect[]
    )

    yield all(syncAllSamplesActions)
    yield call(getTrackers_legacy)
  } catch (error) {
    logger.error('Failed to sync samples for trackers', {}, error)
  }
}

export function* healthKitSyncRequest(): Generator {
  let isBackgrounded = undefined

  try {
    const trackerName = WellnessCenter.Trackers.Types.TrackerName.AppleHealthkit

    const shouldTrackerSyncBeIgnored = yield select(selectShouldTrackerSyncBeIgnored)
    if (shouldTrackerSyncBeIgnored) {
      return
    }

    isBackgrounded = yield select(selectIsBackgrounded)
    if (isBackgrounded) {
      yield call(checkRenewAuth)
    }

    const trackersState = yield select(selectTrackers)
    const noTrackerData = [trackersState?.trackers?.user, trackersState?.trackers?.supported].every(
      (trackerGroup) => !trackerGroup?.length
    )

    yield call(syncAllSamplesForTrackers, {
      needsSyncTrackerStatus: noTrackerData || !isBackgrounded,
      trackersWithSyncOptions: {
        [trackerName]: SYNC_ALL_SAMPLES_APPLE_HEALTH_KIT_ARGS(moment()),
      },
    })
  } catch (error) {
    logger.error('HealthKit sync request failed', {}, error)
  }
}

/**
 * Creates a Channel for events from event sources from Apple Health Kit
 * See https://redux-saga.js.org/docs/advanced/Channels
 */
export function createAppleHealthKitChannel(): Generator {
  const trackerName = WellnessCenter.Trackers.Types.TrackerName.AppleHealthkit

  const errorHandler = (err: Error, activity: string) => {
    logger.error('HealthKit activity listener failed', { tags: { trackerName, activity } }, err)
  }

  // @ts-expect-error FIXME: EventChannel is not compatible with Generator
  return eventChannel((emit) =>
    AppleHealthKit.listenToAllActivities((err, activity) => {
      err ? errorHandler(err, activity) : emit({ activity })
    })
  )
}

export function* listenToHealthKitBackgroundUpdates(): Generator {
  let channel
  try {
    channel = yield call(createAppleHealthKitChannel)
    while (true) {
      const { activity } = yield take(channel)
      yield put(WellnessCenterActions.healthKitSyncRequest(`trigger: new metrics for ${activity}`))
    }
  } finally {
    if (yield cancelled() && channel) {
      channel.close()
    }
  }
}

export function* listenToForegroundingActions(): Generator {
  while (true) {
    yield take(AppSessionTypes.SET_APP_FOREGROUNDED)
    yield put(WellnessCenterActions.healthKitSyncRequest('trigger: foregrounding'))
  }
}

const createTrackerStatusChangeActionPatterns = (trackerName: string) => ({
  isConnected: (action: AnyAction) =>
    action.type === WellnessCenterTypes.GET_TRACKERS_SUCCESS &&
    (action.trackers as WellnessCenterState['trackers']['trackers'])?.user?.some(
      ({ tracker_name, status }) =>
        tracker_name === trackerName &&
        status === WellnessCenter.Trackers.Types.TrackerStatus.Connected
    ),
  isDisconnected: (action: AnyAction) =>
    action.type === WellnessCenterTypes.GET_TRACKERS_SUCCESS &&
    (action.trackers as WellnessCenterState['trackers']['trackers'])?.supported?.some(
      ({ tracker_name }) => tracker_name === trackerName
    ),
  isAboutToConnect: (action: AnyAction) =>
    action.type === WellnessCenterTypes.CONNECT_TRACKER && action.trackerName === trackerName,
})

export function* watchForHKSyncOpportunities(initialIsElligible: boolean): Generator {
  let isElligible = initialIsElligible
  // if not elligible form the start, wait for elligibility update
  while (!isElligible) {
    yield take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS)
    const nextServiceFeatures = yield select(contentSelectors.selectServiceFeatures)
    isElligible = nextServiceFeatures.fitness_trackers
  }

  const trackerName = WellnessCenter.Trackers.Types.TrackerName.AppleHealthkit
  const HealthKitPatterns = createTrackerStatusChangeActionPatterns(trackerName)

  let trackerStatus = undefined
  while (true) {
    if (!trackerStatus) {
      yield put(WellnessCenterActions.getTrackers())
      trackerStatus = yield race({
        isConnected: take(HealthKitPatterns.isConnected),
        isDisconnected: take(HealthKitPatterns.isDisconnected),
      })
    }

    if (trackerStatus.isDisconnected) {
      yield all([take(HealthKitPatterns.isAboutToConnect), take(HealthKitPatterns.isConnected)])
    }

    yield put(WellnessCenterActions.healthKitSyncRequest(`trigger: first sync`))

    yield race([
      call(listenToHealthKitBackgroundUpdates),
      call(listenToForegroundingActions),
      take(HealthKitPatterns.isDisconnected),
    ])
    trackerStatus = { isDisconnected: true }
  }
}

export function* healthKitSyncProcess(): Generator {
  try {
    yield all([
      take(contentActions.getServiceFeaturesSuccess),
      take(PatientTypes.SCRIBE_V2_USER_FETCH_SUCCESS),
    ])

    const serviceFeatures = yield select(contentSelectors.selectServiceFeatures)
    let isElligible = serviceFeatures.fitness_trackers

    while (true) {
      const watcherProcess = yield fork(watchForHKSyncOpportunities, isElligible)

      yield take(LoginTypes.LOGOUT)
      // reset & go for another loop
      isElligible = false
      yield cancel(watcherProcess)
    }
  } catch (error) {
    logger.error('HealthKit sync watcher failed', {}, error)
  }
}
